import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  Envelope,
  Key,
  Link as LinkIcon,
  LinkBreak,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const OAuthIntegrations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  
  const [providers, setProviders] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Check for OAuth callback success/error
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const successParam = params.get('success');
    const errorParam = params.get('error');
    const provider = params.get('provider');

    if (successParam === 'true' && provider) {
setSuccess(`Successfully connected ${provider}!`);
// Clear query params
navigate('/settings/integrations', { replace: true });
setTimeout(() => setSuccess(null), 5000);
    } else if (errorParam) {
setError(`Failed to connect: ${errorParam}`);
setTimeout(() => setError(null), 5000);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (activeOrganization) {
loadProviders();
loadCredentials();
    }
  }, [activeOrganization]);

  const loadProviders = async () => {
    try {
const response = await fetch(`${BACKEND_URL}/api/oauth/providers`, {
  credentials: 'include'
});
if (response.ok) {
  const data = await response.json();
  setProviders(data.providers || []);
}
    } catch (err) {
console.error('Error loading providers:', err);
    }
  };

  const loadCredentials = async () => {
    if (!activeOrganization) return;
    
    try {
const response = await fetchWithOrg(
  `${BACKEND_URL}/api/oauth/status`,
  {},
  activeOrganization.organization_id
);

if (response.ok) {
  const data = await response.json();
  setCredentials(data.credentials || []);
}
    } catch (err) {
console.error('Error loading credentials:', err);
    } finally {
setLoading(false);
    }
  };

  const handleConnect = async (providerName) => {
    if (!activeOrganization) {
setError('Please select an organization first');
return;
    }

    setConnecting(providerName);
    setError(null);

    try {
const response = await fetchWithOrg(
  `${BACKEND_URL}/api/oauth/connect/${providerName}`,
  {},
  activeOrganization.organization_id
);

if (response.ok) {
  const data = await response.json();
  // Open OAuth URL in current window
  window.location.href = data.authorization_url;
} else {
  const errorData = await response.json();
  setError(errorData.detail || 'Failed to initiate OAuth flow');
  setConnecting(null);
}
    } catch (err) {
console.error('Error connecting provider:', err);
setError('Failed to connect provider');
setConnecting(null);
    }
  };

  const handleDisconnect = async (providerName) => {
    if (!window.confirm(`Are you sure you want to disconnect ${providerName}?`)) {
return;
    }

    setDisconnecting(providerName);
    setError(null);

    try {
const response = await fetchWithOrg(
  `${BACKEND_URL}/api/oauth/disconnect/${providerName}`,
  { method: 'DELETE' },
  activeOrganization.organization_id
);

if (response.ok) {
  setSuccess(`Successfully disconnected ${providerName}`);
  await loadCredentials();
  setTimeout(() => setSuccess(null), 3000);
} else {
  const errorData = await response.json();
  setError(errorData.detail || 'Failed to disconnect provider');
}
    } catch (err) {
console.error('Error disconnecting provider:', err);
setError('Failed to disconnect provider');
    } finally {
setDisconnecting(null);
    }
  };

  const getProviderStatus = (providerName) => {
    const credential = credentials.find(c => c.provider === providerName);
    return credential || null;
  };

  const getProviderIcon = (providerName) => {
    switch (providerName) {
case 'gmail':
  return <Envelope size={24} className="text-[#EA4335]" />;
case 'salesforce':
  return (
    <div className="w-6 h-6 rounded bg-[#00A1E0] flex items-center justify-center text-white text-xs font-bold">
SF
    </div>
  );
default:
  return <LinkIcon size={24} className="text-[#6366F1]" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
case 'connected':
  return (
    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[rgba(20,241,149,0.1)] border border-[rgba(20,241,149,0.3)]">
<CheckCircle size={14} className="text-[#14F195]" />
<span className="text-[#14F195] text-sm font-semibold">Connected</span>
    </span>
  );
case 'error':
  return (
    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)]">
<XCircle size={14} className="text-[#EF4444]" />
<span className="text-[#EF4444] text-sm font-semibold">Error</span>
    </span>
  );
default:
  return (
    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[rgba(249,115,22,0.1)] border border-[rgba(249,115,22,0.3)]">
<WarningCircle size={14} className="text-[#F59E0B]" />
<span className="text-[#F59E0B] text-sm font-semibold">Not Connected</span>
    </span>
  );
    }
  };

  if (loading) {
    return (
<div className="min-h-screen bg-[#161B26] flex items-center justify-center">
  <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
</div>
    );
  }

  return (
<div className="max-w-5xl mx-auto">
    {/* Header */}
    <div className="mb-12">
<div className="flex items-center gap-3 mb-2">
  <Key size={32} className="text-[#6366F1]" />
  <h1 className="text-4xl sm:text-5xl font-bold text-white">OAuth Integrations</h1>
</div>
<p className="text-[#A0AEC0] text-base sm:text-lg">
  Connect Gmail and Salesforce using secure OAuth authentication
</p>
    </div>

    {/* Success Message */}
    <AnimatePresence>
{success && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="mb-6 p-4 rounded-xl bg-[rgba(20,241,149,0.1)] border border-[rgba(20,241,149,0.3)] flex items-center gap-3"
  >
    <CheckCircle size={20} className="text-[#14F195]" />
    <p className="text-[#14F195] text-sm flex-1">{success}</p>
  </motion.div>
)}
    </AnimatePresence>

    {/* Error Message */}
    <AnimatePresence>
{error && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3"
  >
    <WarningCircle size={20} className="text-[#EF4444]" />
    <p className="text-[#EF4444] text-sm flex-1">{error}</p>
  </motion.div>
)}
    </AnimatePresence>

    {/* Provider Cards */}
    <div className="grid md:grid-cols-2 gap-6">
{providers.map((provider) => {
  const status = getProviderStatus(provider.name);
  const isConnected = status?.status === 'connected';
  const isLoading = connecting === provider.name || disconnecting === provider.name;

  return (
      <>
    <motion.div
key={provider.name}
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
className="glass rounded-2xl p-6 hover:bg-[rgba(255,255,255,0.08)] transition-all"
    >
{/* Provider Header */}
<div className="flex items-start justify-between mb-4">
  <div className="flex items-center gap-3">
    {getProviderIcon(provider.name)}
    <div>
<h3 className="text-xl font-bold text-white">
  {provider.display_name}
</h3>
<p className="text-[#A0AEC0] text-sm mt-1">
  {provider.name === 'gmail' && 'Send emails via Gmail API'}
  {provider.name === 'salesforce' && 'Manage CRM contacts and tasks'}
</p>
    </div>
  </div>
</div>

{/* Status Badge */}
<div className="mb-4">
  {getStatusBadge(status?.status)}
</div>

{/* Connection Details */}
{isConnected && status && (
  <div className="mb-4 p-3 rounded-xl bg-[rgba(255,255,255,0.05)] space-y-2">
    {status.connected_email && (
<div>
  <span className="text-[#A0AEC0] text-xs">Connected Account:</span>
  <p className="text-white text-sm font-semibold">{status.connected_email}</p>
</div>
    )}
    {status.connected_at && (
<div>
  <span className="text-[#A0AEC0] text-xs">Connected:</span>
  <p className="text-white text-sm">
    {new Date(status.connected_at).toLocaleDateString()}
  </p>
</div>
    )}
    {status.last_error && (
<div className="p-2 rounded bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)]">
  <span className="text-[#EF4444] text-xs">{status.last_error}</span>
</div>
    )}
  </div>
)}

{/* Action Buttons */}
<div className="flex gap-3">
  {!isConnected ? (
    <button
onClick={() => handleConnect(provider.name)}
disabled={isLoading}
className="flex-1 px-4 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
    >
{isLoading ? (
  <>
    <CircleNotch size={16} className="animate-spin" />
    Connecting...
  </>
) : (
  <>
    <LinkIcon size={16} />
    Connect
  </>
)}
    </button>
  ) : (
    <button
onClick={() => handleDisconnect(provider.name)}
disabled={isLoading}
className="flex-1 px-4 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#EF4444] font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
    >
{isLoading ? (
  <>
    <CircleNotch size={16} className="animate-spin" />
    Disconnecting...
  </>
) : (
  <>
    <LinkBreak size={16} />
    Disconnect
  </>
)}
    </button>
  )}
</div>
    </motion.div>
    </>
  );
})}
    </div>

    {/* Info Box */}
    <motion.div
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
transition={{ delay: 0.2 }}
className="mt-8 p-6 rounded-2xl bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)]"
    >
<h3 className="text-white font-bold mb-2 flex items-center gap-2">
  <WarningCircle size={18} className="text-[#6366F1]" />
  About OAuth Integrations
</h3>
<p className="text-[#A0AEC0] text-sm leading-relaxed">
  OAuth provides secure, token-based authentication without sharing your password. 
  When you connect a service, you'll be redirected to authenticate directly with the provider. 
  Tokens are encrypted and stored securely. You can disconnect at any time.
</p>
    </motion.div>
  </div>
  );
};

export default OAuthIntegrations;
